import React from "react";
import { Navbar as HeroNavbar, NavbarContent, NavbarItem, NavbarBrand, NavbarMenuToggle, NavbarMenu, NavbarMenuItem, Button, Badge, Popover, PopoverTrigger, PopoverContent } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useAuth } from "../contexts/auth-context";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";
import { useCart } from "../contexts/cart-context";
import { ThemeToggle } from './theme-toggle';
import { useCategories, useCatalog } from "../hooks/use-catalog";
import { SearchPopover } from "./search-popover";

const DESKTOP_SEARCH_ANIMATION_MS = 280;

interface NavbarProps {
  onCartClick: () => void;
  currentPage: string; // kept for backward compatibility, no longer used for active state
  onPageChange: (page: string) => void; // kept but not required for navigation
  onProductTypeSelect?: (productType: string) => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  onCartClick,
  currentPage,
  onPageChange,
  onProductTypeSelect
}) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isCatalogPopoverOpen, setCatalogPopoverOpen] = React.useState(false);
  const [isSearchExpanded, setSearchExpanded] = React.useState(false);
  const [shouldRenderSearch, setShouldRenderSearch] = React.useState(false);
  const desktopSearchRef = React.useRef<HTMLDivElement | null>(null);
  const searchToggleRef = React.useRef<HTMLButtonElement | null>(null);
  const history = useHistory();
   const location = useLocation();

  const { categories, isLoading: categoriesLoading } = useCategories();
  const { catalog, isLoading: catalogLoading } = useCatalog();

  const pickProductImage = React.useCallback((product: any) => {
    if (!product) return '';
    if (typeof product.image === 'string' && product.image) return product.image;
    if (Array.isArray(product.images) && product.images.length) {
      const main = product.images.find((img: any) => img?.is_main) || product.images[0];
      if (typeof main === 'string') return main;
      if (main && typeof main.url === 'string') return main.url;
    }
    if (product.main_image) return product.main_image;
    if (product.preview_image) return product.preview_image;
    return '';
  }, []);

  const catalogSections = React.useMemo(() => {
    if (!Array.isArray(categories) || categories.length === 0) return [];
    return categories
      .map((category) => {
        const products = Array.isArray((catalog as any)?.[category.key]) ? (catalog as any)[category.key] : [];
        const image = category.image || pickProductImage(products[0]) || '';
        const productCount = products.length;
        const fallbackDescription = productCount ? `${productCount} позиций` : '';
        return {
          key: category.key,
          title: category.name || category.key,
          description: category.description || fallbackDescription,
          image,
          path: `/catalog/${category.key}`,
          count: productCount,
        };
      })
      .filter((section) => section.title)
      .slice(0, 6);
  }, [categories, catalog, pickProductImage]);

  const catalogSectionsLoading = categoriesLoading || catalogLoading;

  // Update menu items - remove hasDropdown and submenu from catalog item
  const menuItems = [
    { key: "home", label: "Главная", path: "/" },
    { 
      key: "catalog", 
      label: "Каталог", 
      path: "/catalog"
      // Removed hasDropdown and submenu properties
    },
    { key: "products", label: "Продукция", path: "/products" },
    { key: "about", label: "О нас", path: "/about" },
    { key: "partners", label: "Партнёрам", path: "/partners" },
    { key: "news-list", label: "Новости", path: "/news-list" },
    { key: "contacts", label: "Контакты", path: "/contacts" },
  ];

  // Get auth context
  const { isAuthenticated, user, logout } = useAuth();

  // Add cart context
  const { totalItems } = useCart();

  const handleProductSelect = (productType: string) => {
    setIsMenuOpen(false);
    history.push(`/product/${productType}`);
    // Still notify parent component
    if (onProductTypeSelect) {
      onProductTypeSelect(productType);
    }
  };

  // Simplify handleNavigation function - treat catalog like any other page
  const handleNavigation = (page: string) => {
    setIsMenuOpen(false);
    
    // Find the menu item and navigate to its path
    const menuItem = menuItems.find(item => item.key === page);
    if (menuItem) {
      history.push(menuItem.path);
    }
  };

  const handleCatalogNavigate = (path: string) => {
    setCatalogPopoverOpen(false);
    setIsMenuOpen(false);
    history.push(path);
  };

  // Remove the useEffect that handles clicking outside to close dropdown
  // since we no longer have a dropdown to close

  const handleLogout = () => {
    logout();
    history.push('/');
  };

  React.useEffect(() => {
    if (!isSearchExpanded) return;
    const handleClick = (event: MouseEvent) => {
      if (desktopSearchRef.current?.contains(event.target as Node)) return;
      if (searchToggleRef.current?.contains(event.target as Node)) return;
      setSearchExpanded(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSearchExpanded(false);
      }
    };
    const handleFocusIn = (event: FocusEvent) => {
      if (desktopSearchRef.current?.contains(event.target as Node)) return;
      if (searchToggleRef.current?.contains(event.target as Node)) return;
      setSearchExpanded(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    document.addEventListener('focusin', handleFocusIn);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('focusin', handleFocusIn);
    };
  }, [isSearchExpanded]);

  React.useEffect(() => {
    if (isSearchExpanded) {
      setShouldRenderSearch(true);
      return;
    }
    const timeout = setTimeout(() => setShouldRenderSearch(false), DESKTOP_SEARCH_ANIMATION_MS);
    return () => clearTimeout(timeout);
  }, [isSearchExpanded]);

  React.useEffect(() => {
    setSearchExpanded(false);
  }, [location.pathname]);

  return (
    <HeroNavbar 
      isMenuOpen={isMenuOpen} 
      onMenuOpenChange={setIsMenuOpen}
      maxWidth="xl"
      className="bg-background/80 backdrop-blur-md border-b border-divider sticky top-0 z-50"
    >
      <NavbarContent className="sm:hidden" justify="start">
        <NavbarMenuToggle aria-label="Open menu" />
      </NavbarContent>

      <NavbarContent className="sm:hidden pr-3" justify="center">
        <NavbarBrand>
          <RouterLink to="/" className="font-bold text-inherit tracking-widest">
            SAFONOVA
          </RouterLink>
        </NavbarBrand>
      </NavbarContent>

      <NavbarContent className="hidden sm:flex gap-8" justify="center">
        <NavbarBrand>
          <RouterLink to="/" className="font-bold text-inherit tracking-widest">
            SAFONOVA
          </RouterLink>
        </NavbarBrand>
        {menuItems.map((item) => (
          (() => {
            const isActive = location.pathname === item.path;

            if (item.key === "catalog") {
              return (
                <NavbarItem key={item.key} isActive={isActive} className="relative">
                  <Popover
                    isOpen={isCatalogPopoverOpen}
                    onOpenChange={setCatalogPopoverOpen}
                    placement="bottom-start"
                    offset={12}
                    showArrow
                    backdrop="opaque"
                    classNames={{ content: "p-0 border border-divider/60 rounded-none shadow-2xl bg-background/95 w-[960px] max-w-[95vw]" }}
                  >
                    <PopoverTrigger>
                      <button
                        type="button"
                        className={`flex items-center gap-1 text-foreground/90 hover:text-foreground nav-link ${isActive ? 'active-nav-link' : ''}`}
                        aria-haspopup="dialog"
                        aria-expanded={isCatalogPopoverOpen}
                      >
                        {item.label}
                        <Icon icon="lucide:chevron-down" width={16} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent>
                      <div className="p-6 space-y-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                          <div>
                            <p className="text-xs uppercase tracking-[0.3em] text-foreground/50">Каталог</p>
                            <h3 className="text-2xl font-semibold mt-1">Найдите аромат под настроение</h3>
                            <p className="text-foreground/70">Выберите раздел и сразу переходите к нужной коллекции</p>
                          </div>
                          <Button
                            variant="flat"
                            color="primary"
                            endContent={<Icon icon="lucide:arrow-up-right" width={16} />}
                            onPress={() => handleCatalogNavigate(item.path)}
                          >
                            Перейти в каталог
                          </Button>
                        </div>

                        {catalogSections.length > 0 ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            {catalogSections.map((section) => (
                              <RouterLink
                                key={section.key}
                                to={section.path}
                                className="group relative flex flex-col gap-3 border border-divider/60 bg-content1/95 p-4 transition hover:border-primary/50"
                                onClick={(event) => {
                                  event.preventDefault();
                                  handleCatalogNavigate(section.path);
                                }}
                              >
                                <div className="relative h-32 bg-content2/80 overflow-hidden">
                                  {section.image ? (
                                    <img
                                      src={section.image}
                                      alt={section.title}
                                      loading="lazy"
                                      className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                                    />
                                  ) : (
                                    <div className="h-full w-full flex items-center justify-center text-xs text-foreground/50">
                                      Фото появится позже
                                    </div>
                                  )}
                                </div>
                                <div className="relative space-y-1">
                                  <p className="text-xs uppercase tracking-[0.2em] text-foreground/60">{section.count ? `${section.count} товаров` : 'Коллекция'}</p>
                                  <h4 className="text-lg font-semibold text-foreground">{section.title}</h4>
                                </div>
                              </RouterLink>
                            ))}
                          </div>
                        ) : (
                          <div className="rounded-2xl border border-dashed border-divider/80 px-6 py-14 text-center text-sm text-foreground/60">
                            {catalogSectionsLoading ? 'Загружаем разделы…' : 'Категории временно недоступны'}
                          </div>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </NavbarItem>
              );
            }

            return (
              <NavbarItem 
                key={item.key} 
                isActive={isActive}
                className="relative"
              >
                <RouterLink 
                  to={item.path}
                  className={`text-foreground/90 hover:text-foreground nav-link ${isActive ? 'active-nav-link' : ''}`}
                  onClick={() => {
                    setIsMenuOpen(false);
                  }}
                >
                  {item.label}
                </RouterLink>
              </NavbarItem>
            );
          })()
        ))}
      </NavbarContent>

      <NavbarContent justify="end" className="gap-2">
        {/* <NavbarItem>
          <ThemeToggle />
        </NavbarItem> */}
        <NavbarItem className="hidden lg:flex items-center">
          <div className="flex items-center gap-2">
            <div
              ref={desktopSearchRef}
              className={`relative transition-[width,opacity] duration-300 ease-out ${isSearchExpanded ? 'w-[360px] opacity-100' : 'w-0 opacity-0 pointer-events-none'}`}
            >
              {shouldRenderSearch && (
                <SearchPopover
                  variant="inline"
                  className="w-full"
                  autoFocusInline
                  forceInlineOpen={isSearchExpanded}
                  onInlineDismiss={() => setSearchExpanded(false)}
                  onNavigate={(path) => {
                    setIsMenuOpen(false);
                    setCatalogPopoverOpen(false);
                    setSearchExpanded(false);
                    history.push(path);
                  }}
                />
              )}
            </div>
            <Button
              ref={searchToggleRef}
              isIconOnly
              variant="light"
              aria-label={isSearchExpanded ? 'Закрыть поиск' : 'Открыть поиск'}
              aria-pressed={isSearchExpanded}
              className="text-foreground/80"
              onPress={() => setSearchExpanded((prev) => !prev)}
            >
              <Icon icon={isSearchExpanded ? 'lucide:x' : 'lucide:search'} width={18} />
            </Button>
          </div>
        </NavbarItem>
        <NavbarItem className="lg:hidden">
          <SearchPopover
            onNavigate={(path) => {
              setIsMenuOpen(false);
              setCatalogPopoverOpen(false);
              history.push(path);
            }}
          />
        </NavbarItem>
        {isAuthenticated ? (
          <NavbarItem>
            <div className="relative inline-flex">
              <Button
                isIconOnly
                variant="light"
                aria-label="User account"
                className="text-foreground/90 transition-colors bg-primary/5 border border-primary/30 text-primary"
                onPress={() => history.push("/account")}
              >
                <Icon icon="lucide:user" width={20} />
              </Button>
              <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-success shadow-[0_0_0_2px_var(--heroui-background)]" />
            </div>
          </NavbarItem>
        ) : (
          <NavbarItem>
            <Button
              isIconOnly
              variant="light"
              aria-label="User"
              className="text-foreground/90"
              onPress={() => history.push("/auth")}
            >
              <Icon icon="lucide:user" width={20} />
            </Button>
          </NavbarItem>
        )}
        <NavbarItem>
          <Badge content={totalItems} color="primary" size="sm">
            <Button
              isIconOnly
              variant="light"
              aria-label="Cart"
              className="text-foreground/90"
              onPress={onCartClick}
            >
              <Icon icon="lucide:shopping-bag" width={20} />
            </Button>
          </Badge>
        </NavbarItem>
      </NavbarContent>

      <NavbarMenu className="bg-background/95 backdrop-blur-lg pt-6">
        {menuItems.map((item) => (
          <NavbarMenuItem key={item.key}>
            <RouterLink
              to={item.path}
              className="block w-full text-foreground/90 hover:text-foreground py-2 text-lg"
              onClick={() => {
                setIsMenuOpen(false);
              }}
            >
              {item.label}
            </RouterLink>
          </NavbarMenuItem>
        ))}
      </NavbarMenu>
    </HeroNavbar>
  );
};